<script setup lang="ts">
import type { UniquePost } from "@/server/api/types";
import { setSEO } from "@/utils/seoUtils";
import { ROUTE_BLOG_CATEGORY } from "~/constants/routes";

const route = useRoute();

definePageMeta({
  layout: "blog-page-slug",
  scrollToTop: true,
});
const config = useRuntimeConfig();
const { replaceParams } = useRouteParams();
const slug = computed(() => {
  return route.params.slug as string;;
});

const { data } = await useLazyAsyncData(
  `blog-post-${slug.value}`,
  async () => {
    const [categoriesResponse, postResponse] = await Promise.all([
      $fetch("/api/content/categories"),
      $fetch(`/api/content/posts/${route.params.slug}`),
    ]);

    return {
      categories: Object.values(categoriesResponse) ?? [],
      post: postResponse,
    };
  },
  { watch: [slug] }
);

const categories = computed(() => data?.value?.categories ?? []);

const post = computed(() => data?.value?.post ?? null);

setSEO({
  seo: post.value?.seo,
  modifiedAt: post.value?.modified,
  publishedAt: post.value?.date,
});

const postCategoriesLength = computed(
  () => post?.value?.categories?.nodes?.length ?? 0
);

const relatedPosts = computed(() => {
  if (!post.value) {
    return [];
  }
  const currentPostSlug = route.params.slug;
  const postsByTag = post.value?.tags?.edges?.flatMap(
    (tag) => tag.node.posts.edges
  );
  const uniquePosts: UniquePost[] = [];

  postsByTag.forEach((postEdge) => {
    const post = postEdge.node;
    if (
      !uniquePosts.find((uniquePost) => uniquePost.id === post.id) &&
      post.slug !== currentPostSlug
    ) {
      uniquePosts.push({
        id: post?.id,
        slug: post?.slug,
        title: post?.title,
        link: `/blog/${post?.slug}/`,
        categories: post?.categories?.nodes,
        excerpt: post?.excerpt,
        featuredImage: post?.featuredImage?.node?.sourceUrl,
      });
    }
  });

  return uniquePosts;
});

const backgroundHeaderDesktopUrl = computed(() => `url(${post.value?.featuredImage?.node?.sourceUrl})`);

const shareLinkUrl = `${config.public.baseUrl}/blog/${slug.value}`;
</script>

<template>
  <section
    data-bg
    class="tw-text-center tw-py-[41px] sm:tw-pt-[74px] sm:tw-pb-[46px] tw-relative tw-tw-bg-nl-yellow-100 tw-flex tw-items-center tw-bg-cover tw-bg-center background-header"
  >
    <span class="tw-inline tw-min-h-[159px] tw-w-0"></span>
  </section>
  <section>
    <BlogPageCategoryNavbar :categories="categories">
      <template #search> <BlogSearch /> </template>
    </BlogPageCategoryNavbar>
    <div class="tw-max-w-[1180px] tw-px-[17px] tw-mx-auto">
      <article class="md:tw-flex tw-pb-10 sm:tw-pb-[60px] md:tw-pb-20 tw-text-[14px] tw-leading-[1.36]">
        <div class="md:tw-flex-1 md:tw-pl-[69px] md:tw-pr-[51px] tw-relative">
          <header class="tw-mb-[15px]">
            <span
              class="tw-block tw-font-bold tw-font-agendaCond tw-text-[14px] tw-tracking-[0.09em] tw-uppercase tw-mb-[9px]"
            >
              <span class="tw-text-nl-green-200">Categories: </span>
              <template
                v-for="(category, index) in post?.categories?.nodes"
                :key="category?.name"
              >
                <nuxt-link
                  :to="{path: replaceParams(ROUTE_BLOG_CATEGORY, {slug: category?.slug})}"
                  rel="category tag"
                >
                  {{ category?.name }}
                </nuxt-link>
                <span v-if="index !== postCategoriesLength - 1">, </span>
              </template>
            </span>

            <h1 class="tw-font-bold tw-text-[1.875rem] tw-leading-[1.13] tw-mb-4">
              {{ post?.title }}
            </h1>
            <time
              v-if="post?.date"
              class="tw-block tw-font-bold tw-font-agendaCond tw-text-[14px] tw-uppercase tw-mb-[22px] md:tw-mb-0"
            >{{ formatDate(post?.date) }}</time
            >
            <ShareButtons :link="shareLinkUrl" :slug="slug"/>
          </header>
          <div class="wysiwyg [&_h1]:tw-text-[1.875rem] [&_h1]:tw-leading-[1.13] [&_h2]:tw-text-[24px] [&_h3]:tw-text-[18px] [&_h4]:tw-text-[16px] [&_h5]:tw-text-[15px] [&_h6]:tw-text-[14px] [&_strong]:tw-font-bold [&_ul:not(.list-none)]:tw-list-disc [&_ul:not(.list-none)]:tw-pl-[30px] [&_ol:not(.list-none)]:tw-list-decimal [&_ol:not(.list-none)]:tw-pl-[30px] [&_a]:tw-underline [&_a:hover]:tw-no-underline [&_a]:tw-text-nl-green-200 [&_p]:tw-font-bookmania" v-html="post?.content"></div>
          <footer
            class="tw-overflow-hidden tw-border-b md:tw-border-b-0 tw-border-t tw-border-gray-[#707070]/20 tw-my-[25px] tw-pt-7 tw-pb-0.5 sm:tw-pt-9 sm:tw-mt-[38px] sm:tw-mb-[30px] tw-text-[13px] sm:tw-text-[14px] tw-leading-[1.57]"
          >
            <div class="tw-float-left tw-mr-[23px] tw-w-[74px] sm:tw-w-[128px]">
              <img
                v-if="post?.author?.node?.authorMeta?.avatar?.node?.sourceUrl"
                :src="post?.author?.node?.authorMeta?.avatar?.node?.sourceUrl"
                :alt="post?.author?.node?.authorMeta?.avatar?.node?.altText"
                class="attachment-avatar"
              />
            </div>
            <div class="sm:tw-overflow-hidden sm:tw-py-4">
              <h3
                v-if="post?.author?.node?.name"
                class="tw-text-[15px] sm:tw-text-[18px] tw-mb-1.5 sm:tw-mb-3 tw-font-bold"
              >
                {{ post?.author?.node?.name }}
              </h3>
              <p v-if="post?.author?.node?.description">
                {{ post?.author?.node?.description }}
              </p>
            </div>
          </footer>
          <div>
            <NuxtLink
              to="/blog/"
              class="tw-text-nl-green-100 hover:tw-text-nl-green-200 tw-inline-flex tw-items-center tw-align-top tw-font-medium tw-font-agendaCond tw-text-[14px] tw-uppercase"
            ><i class="icon-main-chevron-a-left tw-text-[13px] tw-mr-[11px]"></i>
              <span data-back-to-posts-link class="tw-underline"
              >Back to Posts</span
              ></NuxtLink
            >
          </div>
        </div>
        <div class="md:tw-w-[195px] tw-pt-[30px] sm:tw-pt-[60px] md:tw-pt-0">
          <SidebarSubscribeForm/>
        </div>
      </article>
    </div>
  </section>
  <section
    v-if="relatedPosts.length > 0"
    class="tw-pt-12 sm:tw-pt-[71px] tw-bg-nl-green-200/[0.05] -tw-mb-[93px] tw-pb-[129px]"
  >
    <div class="tw-container">
      <h3
        class="tw-text-center tw-font-black tw-font-bookmania tw-text-[30px] tw-leading-[1.12] tw-tracking-[0.02em] tw-mb-6 sm:tw-mb-[34px]"
      >
        Related Posts
      </h3>
      <BlogRelatedPostList :related-posts="relatedPosts" />
    </div>
  </section>
</template>

<style scoped>
.background-header {
  background-image: v-bind(backgroundHeaderDesktopUrl);
}
</style>
